import React from 'react'
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
  useTheme,
  useMediaQuery,
  alpha
} from '@mui/material'
import {
  Dashboard as DashboardIcon,
  School as SchoolIcon,
  PlayArrow as DemoIcon,
  CheckCircle as BilledIcon,
  Cancel as NotBilledIcon,
  Warning as ExpiredIcon,
  Schedule as TrainingIcon,
  AttachMoney as RevenueIcon,
  Assessment as AnalyticsIcon,
  Description as ContractsIcon,
  Refresh as RenewalsIcon,
  ContactSupport as SupportIcon,
  Settings as SettingsIcon,
  Menu as MenuIcon,
  Close as CloseIcon,
  WhatsApp as WhatsAppIcon,
  AccountCircle as AccountCircleIcon,
  History as HistoryIcon,
  ShoppingCart as ShoppingCartIcon,
  Help as HelpIcon,
  Group as GroupIcon,
  Phone as PhoneIcon,
  SmartToy as AgentIcon,
  People as ReferralIcon,
  Campaign as PromoterIcon
} from '@mui/icons-material'
import { useNavigate, useLocation } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useAuth } from '../../contexts/AuthContext'
import { APP_HEADER_HEIGHT } from './layoutConstants'

const drawerWidth = 280

// Menu items based on user role - CRM Structure
const getMenuItems = (userRole) => {
  const baseItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/', roles: ['ADMIN', 'TL', 'TRAINER'] },
    { text: 'All Schools', icon: <SchoolIcon />, path: '/schools', roles: ['ADMIN', 'TL', 'TRAINER'] },
    { text: 'Demo Schools', icon: <DemoIcon />, path: '/schools/demo', roles: ['ADMIN', 'TL'] },
    { text: 'Billed Schools', icon: <BilledIcon />, path: '/schools/billed', roles: ['ADMIN', 'TL'] },
    { text: 'Not Billed', icon: <NotBilledIcon />, path: '/schools/not-billed', roles: ['ADMIN', 'TL'] },
    { text: 'Expired Schools', icon: <ExpiredIcon />, path: '/schools/expired', roles: ['ADMIN', 'TL'] },
    { text: 'Training', icon: <TrainingIcon />, path: '/training', roles: ['ADMIN', 'TL', 'TRAINER'] },
  ]

  const adminItems = [
    { text: 'Revenue', icon: <RevenueIcon />, path: '/revenue', roles: ['ADMIN'] },
    { text: 'Analytics', icon: <AnalyticsIcon />, path: '/analytics', roles: ['ADMIN'] },
    { text: 'Contracts', icon: <ContractsIcon />, path: '/contracts', roles: ['ADMIN'] },
    { text: 'Renewals', icon: <RenewalsIcon />, path: '/renewals', roles: ['ADMIN'] },
    { text: 'Support Tickets', icon: <SupportIcon />, path: '/support', roles: ['ADMIN', 'TL'] },
    { text: 'WhatsApp Messaging', icon: <WhatsAppIcon />, path: '/whatsapp', roles: ['ADMIN', 'TL'] },
    { text: 'WhatsApp Queue', icon: <WhatsAppIcon />, path: '/whatsapp/queue', roles: ['ADMIN', 'TL'] },
    { text: 'WhatsApp Inbox', icon: <WhatsAppIcon />, path: '/whatsapp/inbox', roles: ['ADMIN', 'TL'] },
    { text: 'School Accounts', icon: <AccountCircleIcon />, path: '/school-accounts', roles: ['ADMIN'] },
    { text: 'Login Logs', icon: <HistoryIcon />, path: '/login-logs', roles: ['ADMIN'] },
    { text: 'My Orders', icon: <ShoppingCartIcon />, path: '/my-orders', roles: ['ADMIN', 'TL'] },
    { text: 'School Query', icon: <HelpIcon />, path: '/school-query', roles: ['ADMIN', 'TL'] },
    { text: 'School Groups', icon: <GroupIcon />, path: '/school-groups', roles: ['ADMIN', 'TL'] },
    { text: 'Telecaller Management', icon: <PhoneIcon />, path: '/telecaller', roles: ['ADMIN', 'TL'] },
    { text: 'AI Agents', icon: <AgentIcon />, path: '/ai-agents', roles: ['ADMIN'] },
    { text: 'Referral Management', icon: <ReferralIcon />, path: '/referrals', roles: ['ADMIN', 'TL'] },
    { text: 'Promoter Management', icon: <PromoterIcon />, path: '/promoters', roles: ['ADMIN', 'TL'] },
  ]

  const allItems = [...baseItems, ...adminItems, { text: 'Settings', icon: <SettingsIcon />, path: '/settings', roles: ['ADMIN', 'TL', 'TRAINER'] }]

  // Filter menu items based on user role
  return allItems.filter(item => item.roles.includes(userRole))
}

const Sidebar = ({ open, onToggle }) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const navigate = useNavigate()
  const location = useLocation()
  const { user, getUserRole } = useAuth()
  
  const userRole = getUserRole() || 'ADMIN'
  const menuItems = getMenuItems(userRole)

  const handleNavigation = (path) => {
    navigate(path)
    if (isMobile) {
      onToggle()
    }
  }

  const drawerContent = (
    <Box className="sidebar-nav" sx={{ minHeight: 'min-content' }}>
        <List sx={{ px: 2, py: 1 }}>
          {menuItems.map((item, index) => {
            const isActive = location.pathname === item.path
            return (
              <motion.div
                key={item.text}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <ListItem disablePadding sx={{ mb: 1 }}>
                  <ListItemButton
                    onClick={() => handleNavigation(item.path)}
                    sx={{
                      borderRadius: 3,
                      backgroundColor: isActive 
                        ? 'rgba(255, 255, 255, 0.15)' 
                        : 'transparent',
                      background: isActive 
                        ? 'rgba(255, 255, 255, 0.15)' 
                        : 'transparent',
                      color: isActive ? '#ffffff' : 'rgba(255, 255, 255, 0.85)',
                      borderLeft: isActive ? `3px solid #ffffff` : '3px solid transparent',
                      fontWeight: isActive ? 600 : 400,
                      transition: 'all 0.3s ease-in-out',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        background: 'rgba(255, 255, 255, 0.1)',
                        transform: 'translateX(4px)',
                        color: '#ffffff',
                      },
                      '& .MuiListItemIcon-root': {
                        color: isActive ? '#ffffff' : 'rgba(255, 255, 255, 0.7)',
                        transition: 'color 0.3s ease-in-out',
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText 
                      primary={item.text}
                      primaryTypographyProps={{
                        fontWeight: isActive ? 600 : 400,
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              </motion.div>
            )
          })}
        </List>
    </Box>
  )

  if (isMobile) {
    return (
      <Drawer
        anchor="left"
        open={open}
        onClose={onToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            borderRight: `1px solid ${theme.palette.primary.dark}`,
            background: `linear-gradient(180deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
            color: '#ffffff',
            top: `${APP_HEADER_HEIGHT}px`,
            height: `calc(100vh - ${APP_HEADER_HEIGHT}px)`,
            height: `calc(100dvh - ${APP_HEADER_HEIGHT}px)`,
            overflowY: 'auto',
          },
        }}
      >
        {drawerContent}
      </Drawer>
    )
  }

  // Desktop: side nav in body row — scrolls independently from main content
  return (
    <Box
      component="nav"
      className={`app-sidebar${open ? '' : ' app-sidebar--collapsed'}`}
      aria-hidden={!open}
      sx={{
        borderRight: open ? `1px solid ${theme.palette.primary.dark}` : 'none',
        background: `linear-gradient(180deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
        color: '#ffffff',
        transition: 'width 0.2s ease',
      }}
    >
      <Box sx={{ width: drawerWidth }}>{drawerContent}</Box>
    </Box>
  )
}

export default Sidebar
